import {
  ArrowDownLeft,
  ArrowUpRight,
  Bell,
  CreditCard,
  Landmark,
  LineChart,
  Menu,
  ShieldCheck,
  UsersRound,
  WalletCards,
} from 'lucide-react';

const mobileActivity = [
  { icon: ArrowDownLeft, label: 'Monthly contribution', meta: 'Posted to your record', status: 'Posted' },
  { icon: CreditCard, label: 'Loan request', meta: 'Approval in progress', status: 'In review' },
];

type MobileAppPreviewProps = {
  placement: 'hero' | 'member';
};

function MobileAppPreview({ placement }: MobileAppPreviewProps) {
  const isHero = placement === 'hero';

  return (
    <div
      className={`app-phone app-phone-${placement}`}
      aria-label="Illustration of the CoopCore mobile member experience"
    >
      <div className="app-phone-notch" aria-hidden="true" />
      <div className="app-phone-screen">
        <div className="app-phone-status"><span>9:41</span><span aria-hidden="true">● ▮▮</span></div>

        <div className="app-phone-welcome">
          <div><small>Good morning,</small><strong>Member</strong></div>
          <span className="app-phone-alert">{isHero ? <Bell /> : <ShieldCheck />}</span>
        </div>

        <section className="app-phone-balance">
          <small>{isHero ? 'Available balance' : 'Total savings'}</small>
          <strong>{isHero ? 'Member account' : 'Available balance'}</strong>
          <span>{isHero ? 'Live tenant data' : 'Member account'}</span>
        </section>

        <div className="app-phone-actions" aria-label="Mobile member actions">
          <span><i><ArrowDownLeft /></i><b>Save</b></span>
          <span><i><ArrowUpRight /></i><b>Pay</b></span>
          <span><i><Landmark /></i><b>Loan</b></span>
          <span><i><Menu /></i><b>More</b></span>
        </div>

        <section className="app-phone-activity">
          <div className="app-phone-section-heading"><strong>Recent activity</strong><span>View all</span></div>
          <div className="app-phone-activity-list">
            {mobileActivity.map(({ icon: Icon, label, meta, status }) => (
              <article key={label}>
                <i><Icon /></i>
                <div><strong>{label}</strong><small>{meta}</small></div>
                <em>{status}</em>
              </article>
            ))}
          </div>
        </section>

        <nav className="app-phone-nav" aria-label="Illustrative mobile navigation">
          <span className="active"><WalletCards /><small>Home</small></span>
          <span><UsersRound /><small>Members</small></span>
          <span><CreditCard /><small>Pay</small></span>
          <span><Landmark /><small>Loans</small></span>
          <span><LineChart /><small>More</small></span>
        </nav>
      </div>
    </div>
  );
}

export function ProductPreview() {
  return (
    <div className="hero-product" aria-label="Illustration of the CoopCore web dashboard and member mobile application">
      <div className="hero-product-grid" aria-hidden="true" />
      <div className="laptop-shell">
        <div className="laptop-camera" />
        <div className="laptop-screen">
          <div className="desktop-app-bar">
            <span className="mini-brand"><img src="/coopcore-icon.png" alt="" /> CoopCore</span>
            <strong>Overview</strong>
            <span className="desktop-actions"><span /><span /><span /></span>
          </div>
          <div className="desktop-layout">
            <aside className="desktop-sidebar">
              {['Overview', 'Members', 'Contributions', 'Loans', 'Investments', 'Payments', 'Accounting', 'Reports', 'Approvals', 'Settings'].map((item, index) => (
                <span className={index === 0 ? 'active' : ''} key={item}><i />{item}</span>
              ))}
            </aside>
            <div className="desktop-main">
              <div className="desktop-stat-grid">
                <article><small>Member records</small><strong>Live</strong><em>Controlled access</em></article>
                <article><small>Contributions</small><strong>Current</strong><em>Connected balances</em></article>
                <article><small>Loan portfolio</small><strong>Active</strong><em>Workflow governed</em></article>
              </div>
              <div className="desktop-chart-grid">
                <article className="portfolio-card">
                  <div className="chart-title"><strong>Portfolio activity</strong><span>Sample workspace</span></div>
                  <div className="chart-legend"><span className="legend-green">Contributions</span><span className="legend-blue">Loans</span><span className="legend-teal">Investments</span></div>
                  <svg viewBox="0 0 420 190" role="img" aria-label="Illustrative trend lines">
                    <defs>
                      <linearGradient id="areaGreen" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#2bd6ad" stopOpacity=".22"/><stop offset="1" stopColor="#2bd6ad" stopOpacity="0"/></linearGradient>
                    </defs>
                    <g className="chart-grid-lines"><path d="M20 25H400M20 70H400M20 115H400M20 160H400"/><path d="M20 20V170M95 20V170M170 20V170M245 20V170M320 20V170M395 20V170"/></g>
                    <path className="area" d="M20 147L72 129L122 135L174 104L224 113L276 80L328 91L400 48L400 170L20 170Z" />
                    <path className="line line-green" d="M20 147L72 129L122 135L174 104L224 113L276 80L328 91L400 48" />
                    <path className="line line-blue" d="M20 157L72 149L122 121L174 134L224 105L276 119L328 84L400 74" />
                    <path className="line line-teal" d="M20 166L72 155L122 146L174 138L224 126L276 112L328 104L400 96" />
                  </svg>
                </article>
                <article className="distribution-card">
                  <strong>Loan distribution</strong>
                  <div className="donut"><span>Portfolio<br/><b>status</b></span></div>
                  <ul><li><i className="good"/>Performing</li><li><i className="watch"/>Watchlist</li><li><i className="late"/>Overdue</li></ul>
                </article>
              </div>
            </div>
          </div>
        </div>
        <div className="laptop-base"><span /></div>
      </div>

      <div className="hero-phone"><MobileAppPreview placement="hero" /></div>
    </div>
  );
}

export function MemberPhonePreview() {
  return <MobileAppPreview placement="member" />;
}
